/**
 * What a subscriber with a cancellation scheduled is told, and when.
 *
 * IMPORTS NOTHING, on purpose. Same contract as calendarStatsText.js and
 * calendarWelcomeText.js: a module with no imports can be lifted out and
 * evaluated by scripts/check-calendar.js, so the exact words a paying customer
 * reads are asserted by a gate rather than eyeballed once.
 *
 * WHY THIS EXISTS. The first real cancellation went through and nothing said
 * so anywhere. The account page still offered an upgrade and still said "Next
 * charge" against a date that was not a charge at all but the day access ends,
 * and the master admin Subscribers list showed a plain Active badge identical
 * to every other. Two screens, the same fact, and neither of them had it.
 *
 * THE RULE HAS TWO HALVES AND BOTH MATTER. A cancellation is worth showing only
 * while there is still access to lose. Migration 061 keeps cancel_effective_at
 * after the cancellation lands, deliberately, because when it ended is worth
 * knowing, which means a cancelled row still carries a date. Showing that date
 * as a future promise is how somebody gets told their access ends next month
 * when it ended last week.
 *
 * UTC, NOT THE BROWSER'S ZONE, for the reason calendarWelcomeText.js gives at
 * length: every date on this product is computed in UTC, and local formatting
 * moves it back a day for every subscriber in the United States.
 */

/**
 * The statuses that can still read the calendar.
 *
 * Mirrors READING_STATUSES in server/lib/calendarSubscribers.js. Duplicated
 * rather than fetched because this is a pure display rule that has to run on a
 * row already in hand, and check:calendar asserts the two lists agree.
 */
export const CALENDAR_READING_STATUSES = ['active', 'trial', 'past_due'];

/** A date as a person reads it, or '' when there is not a usable one. */
export function formatCancelDate(value) {
  if (!value) return '';
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleDateString('en-US', {
    timeZone: 'UTC',
    month: 'long',
    day: 'numeric',
    year: 'numeric'
  });
}

/**
 * The date a scheduled cancellation takes access away, or null.
 *
 * The browser half of cancelsAtPeriodEnd in server/lib/calendarSubscribers.js,
 * and it answers the same way for the same row. The master admin list needs it
 * because it reads raw subscriber rows; the account page gets the server's
 * answer already computed and uses the wording helpers below instead.
 *
 * Takes the database column names, because that is the shape the admin list is
 * handed. An unrecognised status reads as no access, so a row carrying a value
 * outside the vocabulary shows nothing rather than a promise.
 */
export function cancelsAtPeriodEnd(subscriber) {
  if (!subscriber || !subscriber.cancel_effective_at) return null;
  if (!CALENDAR_READING_STATUSES.includes(subscriber.status)) return null;
  return subscriber.cancel_effective_at;
}

/**
 * The badge suffix in the admin list, or ''.
 *
 * Deliberately short. It sits beside a status badge in a table cell, so it says
 * the one thing the badge cannot and stops.
 */
export function cancelBadgeLabel(subscriber) {
  const date = formatCancelDate(cancelsAtPeriodEnd(subscriber));
  return date ? `Cancels ${date}` : '';
}

/**
 * The subscription line on the account page.
 *
 * ONE FUNCTION FOR BOTH SENTENCES, which is the point: "Next charge" and
 * "Access ends" are the same slot, and the bug being fixed was a page that
 * could show the first when it meant the second. Making them two independent
 * conditions in JSX is how that happens again.
 *
 * Returns '' when there is no date worth printing, and the caller renders
 * nothing rather than an empty line.
 */
export function subscriptionDateLine({ cancelScheduledFor, currentPeriodEnd }) {
  const ends = formatCancelDate(cancelScheduledFor);
  if (ends) return `Access ends ${ends}. You keep full access until then.`;

  const renews = formatCancelDate(currentPeriodEnd);
  if (renews) return `Next charge: ${renews}.`;

  return '';
}

/**
 * What the button under that line offers.
 *
 * 'reactivate' once a cancellation is scheduled, 'cancel' otherwise. The plan
 * switch is a separate control and the server decides whether to offer it, so
 * it is not one of the answers here.
 */
export function subscriptionAction({ cancelScheduledFor }) {
  return cancelScheduledFor ? 'reactivate' : 'cancel';
}
